import { Box } from "@mui/material";
import { useCallback, useReducer } from "react"
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import useDialogModal from "../../../../hooks/useDialogModal";
import AddNewInvoiceDialog from "./newInvoice/addNewInvoiceDialog";
import { InvoiceService } from "../../../../service/invoiceService";
import useAbortController from "../../../../hooks/useAbortController";
import { isLocalDev } from "../../../../utils/environment";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { getInvoicesListColumns } from "./columns";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import validLookup from "../../../../utils/validLookup";
import { toast } from "react-toastify";
import EditInvoiceDialog from "./newInvoice/editInvoiceDialog";
import { DEFAULT_PAGE_SIZE } from "utils";
import { usePreferences } from "hooks/usePreferences";
import AddTaskDialog from "../../../task/AddTaskDialog";
import { useCan } from "hooks/useCan";

export const FRAME_HANDLING_PATIENT_OWN = 'patient own'
export const FRAME_HANDLING_STOCK = 'stock'

export default function InvoiceTab({ customer }) {
    const can = useCan();
    const canCreateInvoice = can.section('invoice', {action: 'create-invoice'});
    const canEditInvoice = can.section('invoice', {action: 'edit-invoice'});
    const canDeleteInvoice = can.section('invoice', {action: 'delete-invoice'});

    const { preferences } = usePreferences();
    const [signal] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
        showTaskDialog: false,
        taskTarget: null,
    });
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [NewInvoiceDialog, showNewInvoiceDialog] = useDialogModal(AddNewInvoiceDialog);
    const [EditInvoiceDialogComp, showEditInvoiceDialogComp] = useDialogModal(EditInvoiceDialog);

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        //updateState({ loading: true });
        InvoiceService.getInvoicesForCustomerWithLimit(from, pageSize, filters, sortBy, customer.customer.id, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            //updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            //updateState({ loading: false });
        });
    }, [customer.customer.id, signal]);

    const handleDeleteClicked = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                try {
                    const deleteRecordResult = await InvoiceService.deleteInvoice(rowids.map(x => parseInt(x, 10)));
                    const { code, message } = deleteRecordResult;

                    if (code === 0) {
                        toast.success(message);
                        const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                        updateState({ data: updateList });
                    } else {
                        toast.success("Failed to delete record(s)");
                    }
                } catch (error) {
                    toast.error("An error occurred while deleting records");
                    if (isLocalDev) {
                        console.error("Error: ", error);
                    }
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const handleAddTask = (rowData) => {
        updateState({ taskTarget: rowData, showTaskDialog: true });
    };

    const handleRowEdit = (rowData) => {
        showEditInvoiceDialogComp(null, {
            invoiceData: rowData,
            onRefresh: () => {
                // Refresh the invoice list by re-fetching current page
                const currentPage = Math.floor(state.data.length > 0 ? 0 : 0); // You might want to track current page
                fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
            },
            onTransferSuccess: async (newInvoiceData) => {
                try {
                    // Fetch the complete invoice data for the new invoice
                    const fullInvoiceResponse = await InvoiceService.getInvoiceItems(newInvoiceData.id);

                    if (fullInvoiceResponse.code === 0 && fullInvoiceResponse.data) {
                        // Close current dialog and open the new transferred invoice
                        setTimeout(() => {
                            showEditInvoiceDialogComp(null, {
                                invoiceData: fullInvoiceResponse.data.invoice,
                                onRefresh: () => {
                                    // Refresh the invoice list
                                    const currentPage = Math.floor(state.data.length > 0 ? 0 : 0);
                                    fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
                                },
                                onTransferSuccess: (newInvoiceData) => {
                                    // Recursive callback for chained transfers
                                    setTimeout(() => {
                                        showEditInvoiceDialogComp(null, {
                                            invoiceData: newInvoiceData,
                                            onRefresh: () => {
                                                const currentPage = Math.floor(state.data.length > 0 ? 0 : 0);
                                                fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
                                            }
                                        });
                                    }, 200);
                                }
                            });
                        }, 200);
                    } else {
                        console.error('Failed to fetch new invoice data:', fullInvoiceResponse.message);
                        // Fallback: just refresh the list
                        const currentPage = Math.floor(state.data.length > 0 ? 0 : 0);
                        fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
                    }
                } catch (error) {
                    console.error('Error fetching new invoice data:', error);
                    // Fallback: just refresh the list
                    const currentPage = Math.floor(state.data.length > 0 ? 0 : 0);
                    fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
                }
            }
        });
    }

    return (
        <Box display="flex" flexDirection="column">

            <Box display="flex" flexDirection="column">
                <EnhancedTable
                    columns={getInvoicesListColumns(handleRowEdit, preferences)}
                    addRecordHandler={canCreateInvoice ? () => showNewInvoiceDialog() : undefined}
                    deleteRecordHandler={canDeleteInvoice ? handleDeleteClicked : undefined}
                    data={state.data}
                    totalRowCount={state.totalRowCount}
                    pageCount={state.pageCount}
                    fetchData={fetchData}
                    loading={state.loading}
                    selectionMode="multi"
                    actions={[
                        {
                            icon: <EditIcon />,
                            onClick: handleRowEdit,
                        },
                        {
                            icon: <PlaylistAddCheckIcon />,
                            onClick: handleAddTask,
                        },
                    ]}
                />
                {/* <InsurancePolicyEditDialog customer={customer} onRecordEdit={recordEditHandler} /> */}

            </Box>
            <NewInvoiceDialog ignoreBackdropClick={true} customer={customer} onRefresh={() => {
                // Refresh the invoice list by re-fetching current page
                const currentPage = Math.floor(state.data.length > 0 ? 0 : 0);
                fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
            }} />
            <EditInvoiceDialogComp ignoreBackdropClick={true} customer={customer} />
            <AddTaskDialog
                open={state.showTaskDialog}
                onClose={() => updateState({ showTaskDialog: false, taskTarget: null })}
                entityType="invoice"
                entityId={state.taskTarget?.id || null}
                onTaskCreated={() => updateState({ showTaskDialog: false })}
            />
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}